import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect, useMemo, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { GalleryItem, GenerationDetailDTO } from '@/lib/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Search, Filter, Heart, Image as ImageIcon, CheckSquare, Square, Trash2 } from 'lucide-react'
import { ImagePopup } from '@/components/image-popup'
import { toast } from 'sonner'

export const Route = createFileRoute('/gallery/')({
  component: GalleryPage,
})

type GalleryAssetEntry = {
  generation_id: string
  asset_index: number
  asset_id: string
  asset_type: string
  prompt: string
  base_model: string
  thumb_url?: string | null
  is_favorite: boolean
  job_type: string
  created_at: string
  full_url?: string | null
  download_url?: string | null
}

function GalleryPage() {
  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedModel, setSelectedModel] = useState<string>('all')
  const [mediaType, setMediaType] = useState<'any' | 'image' | 'video' | 'edit' | 'upscale'>('any')
  const [favoritesOnly, setFavoritesOnly] = useState(false)
  const [selectedImage, setSelectedImage] = useState<GalleryAssetEntry | null>(null)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [showFilters, setShowFilters] = useState(false)
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(true)
  const [selectionMode, setSelectionMode] = useState(false)
  const [selectedGenerationIds, setSelectedGenerationIds] = useState<Set<string>>(new Set())
  const loadMoreRef = useRef<HTMLDivElement>(null)

  // Initial load
  const { data: initialData, isLoading: initialLoading } = useQuery({
    queryKey: ['gallery', 'initial'],
    queryFn: () => api.gallery.onLoad(40),
  })

  // Search with filters
  const { data: searchData, isLoading: searchLoading } = useQuery({
    queryKey: ['gallery', 'search', searchQuery, selectedModel, mediaType, favoritesOnly, cursor],
    queryFn: () => api.gallery.search({
      limit: 40,
      cursor,
      query: searchQuery || null,
      base_model: selectedModel !== 'all' ? selectedModel : null,
      media_type: mediaType,
      favorites_only: favoritesOnly,
    }),
    enabled: searchQuery !== '' || selectedModel !== 'all' || mediaType !== 'any' || favoritesOnly,
  })

  const isLoading = initialLoading || searchLoading

  const assetEntries = useMemo<GalleryAssetEntry[]>(() => {
    const entries: GalleryAssetEntry[] = []

    galleryItems.forEach((item) => {
      if (item.assets && item.assets.length > 0) {
        item.assets.forEach((asset) => {
          const assetIndex = asset.index ?? 0
          entries.push({
            generation_id: item.generation_id,
            asset_index: assetIndex,
            asset_id: asset.asset_id,
            asset_type: asset.asset_type,
            prompt: item.prompt,
            base_model: item.base_model,
            thumb_url: asset.thumb_url ?? item.thumb_url ?? null,
            is_favorite: asset.is_favorite ?? false,
            job_type: item.job_type,
            created_at: item.created_at,
            full_url: asset.full_url ?? undefined,
            download_url: asset.download_url ?? `/api/generation/${item.generation_id}/download/${assetIndex}`,
          })
        })
      } else {
        entries.push({
          generation_id: item.generation_id,
          asset_index: 0,
          asset_id: `${item.generation_id}-0`,
          asset_type: item.job_type === 'video' ? 'video' : 'image',
          prompt: item.prompt,
          base_model: item.base_model,
          thumb_url: item.thumb_url ?? null,
          is_favorite: item.is_favorite,
          job_type: item.job_type,
          created_at: item.created_at,
          full_url: undefined,
          download_url: `/api/generation/${item.generation_id}/download/0`,
        })
      }
    })

    return entries
  }, [galleryItems])

  // Available models for filtering
  const { data: models = [] } = useQuery({
    queryKey: ['gallery', 'models'],
    queryFn: () => api.gallery.models(),
  })

  // Get unique generation IDs from current asset entries (limit to first 50 for performance)
  const generationIds = useMemo(
    () => Array.from(new Set(assetEntries.slice(0, 50).map((item) => item.generation_id))),
    [assetEntries],
  )

  const detailSeed = useMemo(
    () => generationIds.join(','),
    [generationIds],
  )

  // Generation details for popup
  const { data: generationDetails = {} } = useQuery({
    queryKey: ['generation', 'details', detailSeed],
    queryFn: async () => {
      const details: Record<string, GenerationDetailDTO> = {}

      await Promise.all(
        generationIds.map(async (generationId) => {
          try {
            const detail = await api.generation.detail(generationId)
            details[generationId] = detail
          } catch (error) {
            console.error(`Failed to fetch details for ${generationId}:`, error)
          }
        })
      )

      return details
    },
    enabled: generationIds.length > 0,
  })

  // Effect to handle initial data
  useEffect(() => {
    // Only use initial data when no filters are active
    if (initialData?.items && !searchQuery && selectedModel === 'all' && mediaType === 'any' && !favoritesOnly) {
      setGalleryItems(initialData.items)
      setHasMore(!!initialData.next_cursor)
      setCursor(initialData.next_cursor || null)
    }
  }, [initialData])

  // Effect to handle search data
  useEffect(() => {
    // Only use search data when filters are active
    if (searchData?.items && (searchQuery || selectedModel !== 'all' || mediaType !== 'any' || favoritesOnly)) {
      setGalleryItems(searchData.items)
      setHasMore(!!searchData.next_cursor)
      setCursor(searchData.next_cursor || null)
    }
  }, [searchData])

  // Effect to clear gallery items when filters change
  useEffect(() => {
    // Clear gallery items and reset pagination when filters change
    setGalleryItems([])
    setCursor(null)
    setHasMore(true)
  }, [searchQuery, selectedModel, mediaType, favoritesOnly])

  // Infinite scroll with intersection observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0]
        if (target.isIntersecting && hasMore && !isLoading) {
          loadMore()
        }
      },
      {
        threshold: 0.1,
        rootMargin: '100px',
      }
    )

    const currentRef = loadMoreRef.current
    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
    }
  }, [hasMore, isLoading, cursor, searchQuery, selectedModel, mediaType, favoritesOnly])

  const loadMore = () => {
    if (!hasMore || (searchLoading || initialLoading)) return

    if (searchQuery || selectedModel !== 'all' || mediaType !== 'any' || favoritesOnly) {
      // Search mode - use cursor pagination
      if (cursor) {
        api.gallery.search({
          limit: 40,
          cursor,
          query: searchQuery || null,
          base_model: selectedModel !== 'all' ? selectedModel : null,
          media_type: mediaType,
          favorites_only: favoritesOnly,
        }).then((newData) => {
          setGalleryItems(prev => [...prev, ...newData.items])
          setHasMore(!!newData.next_cursor)
          setCursor(newData.next_cursor || null)
        })
      }
    } else {
      // Initial mode - use next cursor
      if (cursor) {
        api.gallery.search({
          limit: 40,
          cursor,
        }).then((newData) => {
          setGalleryItems(prev => [...prev, ...newData.items])
          setHasMore(!!newData.next_cursor)
          setCursor(newData.next_cursor || null)
        })
      }
    }
  }

  const handleImageClick = (image: GalleryAssetEntry, index: number) => {
    setSelectedImage(image)
    setCurrentImageIndex(index)

    // Pre-fetch details if not already loaded
    if (!generationDetails[image.generation_id]) {
      queryClient.prefetchQuery({
        queryKey: ['generation', 'detail', image.generation_id],
        queryFn: () => api.generation.detail(image.generation_id),
      })
    }
  }

  const convertToImageItems = () => {
    return assetEntries.map((item) => {
      const detail = generationDetails[item.generation_id]
      const detailAsset = detail?.assets?.find((asset) =>
        (asset.index ?? 0) === item.asset_index &&
        (asset.asset_type === 'image_full_webp' || asset.asset_type.startsWith('video'))
      )

      const src = detailAsset?.download_url ?? item.full_url ?? item.download_url

      return {
        generationId: item.generation_id,
        assetId: item.asset_id,
        assetType: item.asset_type,
        src,
        thumbnailSrc: item.thumb_url || undefined,
        downloadUrl: item.download_url,
        index: item.asset_index,
        isVideo: item.job_type === 'video',
      }
    })
  }

  
  const handleFavorite = async (assetId: string) => {
    try {
      await api.generation.toggleFavorite(assetId)
      // Refetch gallery data to update favorite status
      queryClient.invalidateQueries({ queryKey: ['gallery'] })
      toast.success('Favorite status updated')
    } catch (error) {
      toast.error('Failed to update favorite')
    }
  }

  const handleRegenerate = async (generationId: string) => {
    try {
      await api.generation.regenerate(generationId)
      toast.success('Image added to generation queue')
      setSelectedImage(null) // Close modal after regenerating
    } catch (error) {
      toast.error('Failed to regenerate image')
    }
  }

  const handleDelete = async (generationId: string) => {
    try {
      await api.generation.delete(generationId)
      // Remove from local state
      setGalleryItems(prev => prev.filter(img => img.generation_id !== generationId))
      queryClient.invalidateQueries({ queryKey: ['gallery'] })
      toast.success('Generation deleted successfully')
    } catch (error) {
      toast.error('Failed to delete generation')
    }
  }

  const toggleSelection = (generationId: string) => {
    setSelectedGenerationIds(prev => {
      const newSet = new Set(prev)
      if (newSet.has(generationId)) {
        newSet.delete(generationId)
      } else {
        newSet.add(generationId)
      }
      return newSet
    })
  }

  const toggleSelectAll = () => {
    if (selectedGenerationIds.size === assetEntries.length) {
      setSelectedGenerationIds(new Set())
    } else {
      const allIds = new Set(assetEntries.map(entry => entry.generation_id))
      setSelectedGenerationIds(allIds)
    }
  }

  const handleBulkDelete = async () => {
    if (selectedGenerationIds.size === 0) return

    const count = selectedGenerationIds.size
    if (!confirm(`Are you sure you want to delete ${count} generation(s)? This action cannot be undone.`)) return

    try {
      const result = await api.generation.bulkDelete(Array.from(selectedGenerationIds))

      // Remove deleted items from local state
      setGalleryItems(prev => prev.filter(img => !selectedGenerationIds.has(img.generation_id)))
      setSelectedGenerationIds(new Set())
      setSelectionMode(false)

      queryClient.invalidateQueries({ queryKey: ['gallery'] })

      if (result.failed_ids.length > 0) {
        toast.warning(`Deleted ${result.deleted_count} generation(s). Failed to delete ${result.failed_ids.length} item(s).`)
      } else {
        toast.success(`Successfully deleted ${result.deleted_count} generation(s)`)
      }
    } catch (error) {
      toast.error('Failed to delete generations')
    }
  }

  const handleImageClickWithSelection = (image: GalleryAssetEntry, index: number) => {
    if (selectionMode) {
      toggleSelection(image.generation_id)
    } else {
      handleImageClick(image, index)
    }
  }

  return (
    <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-primary">Gallery</h1>
          <p className="text-sm text-muted-foreground">
            {selectionMode && selectedGenerationIds.size > 0
              ? `${selectedGenerationIds.size} item(s) selected`
              : 'Browse your generated images, videos, and edits'}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {selectionMode && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={toggleSelectAll}
                className="flex items-center gap-2"
              >
                {selectedGenerationIds.size === assetEntries.length ? (
                  <CheckSquare className="h-4 w-4" />
                ) : (
                  <Square className="h-4 w-4" />
                )}
                {selectedGenerationIds.size === assetEntries.length ? 'Deselect All' : 'Select All'}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleBulkDelete}
                disabled={selectedGenerationIds.size === 0}
                className="flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                Delete ({selectedGenerationIds.size})
              </Button>
            </>
          )}
          <Button
            variant={selectionMode ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              setSelectionMode(!selectionMode)
              if (selectionMode) {
                setSelectedGenerationIds(new Set())
              }
            }}
            className="flex items-center gap-2"
          >
            <CheckSquare className="h-4 w-4" />
            {selectionMode ? 'Done' : 'Select'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2"
          >
            <Filter className="h-4 w-4" />
            Filters
            {(searchQuery || selectedModel !== 'all' || mediaType !== 'any' || favoritesOnly) && (
              <Badge variant="secondary" className="ml-1">
                Active
              </Badge>
            )}
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="space-y-4">
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search images by prompt..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Model Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Model</label>
                  <Select value={selectedModel} onValueChange={setSelectedModel}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Models</SelectItem>
                      {models.map((model) => (
                        <SelectItem key={model} value={model}>
                          {model}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Media Type Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Media Type</label>
                  <Select value={mediaType} onValueChange={(value: any) => setMediaType(value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any Type</SelectItem>
                      <SelectItem value="image">Images</SelectItem>
                      <SelectItem value="video">Videos</SelectItem>
                      <SelectItem value="edit">Edits</SelectItem>
                      <SelectItem value="upscale">Upscales</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Favorites Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Options</label>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="favorites"
                      checked={favoritesOnly}
                      onCheckedChange={(checked) => setFavoritesOnly(checked as boolean)}
                    />
                    <label htmlFor="favorites" className="text-sm">
                      Show only favorites
                    </label>
                  </div>
                </div>

                {/* Clear Filters */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Actions</label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSearchQuery('')
                      setSelectedModel('all')
                      setMediaType('any')
                      setFavoritesOnly(false)
                    }}
                    disabled={!searchQuery && selectedModel === 'all' && mediaType === 'any' && !favoritesOnly}
                  >
                    Clear Filters
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Results Count */}
      {!isLoading && (
        <div className="text-sm text-muted-foreground">
          {assetEntries.length} items found
        </div>
      )}

      {/* Gallery Grid */}
      {isLoading ? (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-12 2xl:grid-cols-12 gap-4">
          {Array.from({ length: 32 }).map((_, i) => (
            <div key={i} className="aspect-square bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      ) : assetEntries.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-muted-foreground">
            {searchQuery || selectedModel !== 'all' || mediaType !== 'any' || favoritesOnly
              ? 'No images found matching your filters'
              : 'No images yet. Start generating to see them here!'}
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-12 2xl:grid-cols-12 gap-4">
            {assetEntries.map((image, index) => {
              const isSelected = selectedGenerationIds.has(image.generation_id)
              return (
                <div
                  key={`${image.generation_id}-${image.asset_index}-${image.asset_id}`}
                  className={`overflow-hidden cursor-pointer hover:opacity-80 transition-all duration-200 group hover:scale-110 z-10 relative ${
                    isSelected ? 'ring-2 ring-primary' : ''
                  }`}
                  onClick={() => handleImageClickWithSelection(image, index)}
                >
                  <div className="relative aspect-square">
                    {image.thumb_url ? (
                      <img
                        src={image.thumb_url}
                        alt={image.prompt}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-muted flex items-center justify-center">
                        <ImageIcon className="h-8 w-8 text-muted-foreground" />
                      </div>
                    )}

                    {/* Selection Checkbox */}
                    {selectionMode && (
                      <div className="absolute top-2 left-2 bg-background/80 rounded p-1">
                        {isSelected ? (
                          <CheckSquare className="h-5 w-5 text-primary" />
                        ) : (
                          <Square className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                    )}

                    {/* Favorite Heart */}
                    {image.is_favorite && !selectionMode && (
                      <div className="absolute top-2 right-2">
                        <Heart className="h-4 w-4 text-red-500 fill-red-500" />
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Infinite Scroll Trigger */}
          {hasMore && (
            <div
              ref={loadMoreRef}
              className="flex justify-center mt-8"
            >
              <div className="text-sm text-muted-foreground">
                {isLoading ? 'Loading...' : 'Scroll for more'}
              </div>
            </div>
          )}
        </>
      )}

      {/* Image Popup */}
      <ImagePopup
        open={!!selectedImage}
        onOpenChange={(open) => !open && setSelectedImage(null)}
        images={convertToImageItems()}
        currentImageIndex={currentImageIndex}
        onImageIndexChange={setCurrentImageIndex}
        generationDetails={generationDetails}
        onRegenerate={handleRegenerate}
        onFavorite={handleFavorite}
        onDelete={handleDelete}
        queryClient={queryClient}
      />
    </div>
  )
}
